import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Chip, 
  Card,
  CardContent,
  CardMedia,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Tooltip
} from '@mui/material';
import { 
  CalendarToday, 
  LocationOn, 
  Business, 
  AttachMoney,
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Delete,
  People,
  TrendingUp,
  Star,
  CheckCircle,
  Warning,
  Error,
  Add,
  Language,
  PhotoLibrary
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getHackathonDetails, 
  updateHackathon, 
  deleteHackathon, 
  getHackathonAnalytics,
  addAdminSponsor,
  getAdminSponsors,
  updateAdminSponsor,
  deleteAdminSponsor
} from '../services/api';

// TypeScript interfaces
interface AdminSponsor {
  _id?: string; // Backend generated ID
  name: string;
  website?: string;
  logo?: string;
}

interface EditingSponsor extends AdminSponsor {
  index?: number;
}

// Update the interface for updateHackathon to exclude adminSponsors (handled separately)
interface UpdateHackathonData {
  status?: string;
  featured?: boolean;
  takeRegistrations?: boolean;
}

const statusOptions = [
  { value: 'pending', label: 'Pending', color: 'warning' },
  { value: 'accepted', label: 'Accepted', color: 'success' },
  { value: 'rejected', label: 'Rejected', color: 'error' },
  { value: 'upcoming', label: 'Upcoming', color: 'info' },
  { value: 'ongoing', label: 'Ongoing', color: 'primary' },
  { value: 'completed', label: 'Completed', color: 'default' },
  { value: 'expired', label: 'Expired', color: 'error' },
] as const;

const HackathonDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [hackathon, setHackathon] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Editable state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    status: '',
    featured: false,
    takeRegistrations: false
  });

  // Admin Sponsor Management State
  const [adminSponsors, setAdminSponsors] = useState<AdminSponsor[]>([]);
  const [sponsorsLoading, setSponsorsLoading] = useState(false);
  const [sponsorDialog, setSponsorDialog] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState<EditingSponsor | null>(null);
  const [sponsorForm, setSponsorForm] = useState<AdminSponsor>({
    name: '',
    website: '',
    logo: ''
  });

  const loadHackathonDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getHackathonDetails(id!);
      if (response.success) {
        setHackathon(response.data);
        setEditData({
          status: response.data.status || 'pending',
          featured: response.data.featured || false,
          takeRegistrations: response.data.takeRegistrations !== false // Default to true
        });
      } else {
        setError('Failed to load hackathon details');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading hackathon details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadAnalytics = useCallback(async () => {
    try {
      const response = await getHackathonAnalytics(id!);
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err: any) {
      console.error('Error loading analytics:', err);
    }
  }, [id]);

  // Load Admin Sponsors separately using dedicated API
  const loadAdminSponsors = useCallback(async () => {
    try {
      setSponsorsLoading(true);
      const response = await getAdminSponsors(id!);
      if (response.success) {
        setAdminSponsors(response.data || []);
      } else {
        // Don't show error for sponsors as it's not critical
        setAdminSponsors([]);
      }
    } catch (err: any) {
      console.error('Error loading admin sponsors:', err);
      // Fallback to empty array if API fails
      setAdminSponsors([]);
    } finally {
      setSponsorsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadHackathonDetails();
      loadAnalytics();
      loadAdminSponsors();
    }
  }, [id, loadHackathonDetails, loadAnalytics, loadAdminSponsors]);

  // Admin Sponsor Management Functions
  const handleAddSponsor = () => {
    setSponsorForm({ name: '', website: '', logo: '' });
    setEditingSponsor(null);
    setSponsorDialog(true);
  };

  const handleEditSponsor = (sponsor: AdminSponsor, index: number) => {
    setSponsorForm({
      name: sponsor.name,
      website: sponsor.website || '',
      logo: sponsor.logo || ''
    });
    setEditingSponsor({ ...sponsor, index });
    setSponsorDialog(true);
  };

  const handleSaveSponsor = async () => {
    try {
      setUpdating(true);
      
      if (editingSponsor && editingSponsor._id) {
        // Update existing sponsor using backend API
        const response = await updateAdminSponsor(id!, editingSponsor._id, sponsorForm);
        if (response.success) {
          setSuccess('Admin sponsor updated successfully');
          // Reload sponsors to get updated data
          await loadAdminSponsors();
        } else {
          setError(response.message || 'Failed to update admin sponsor');
        }
      } else {
        // Add new sponsor using backend API
        const response = await addAdminSponsor(id!, sponsorForm);
        if (response.success) {
          setSuccess('Admin sponsor added successfully');
          // Reload sponsors to include the new one
          await loadAdminSponsors();
        } else {
          setError(response.message || 'Failed to add admin sponsor');
        }
      }
      
      setSponsorDialog(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error saving admin sponsor:', error);
      setError(error.response?.data?.message || 'Failed to save admin sponsor');
      setTimeout(() => setError(null), 5000);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteSponsor = async (sponsor: AdminSponsor, index: number) => {
    if (window.confirm('Are you sure you want to delete this admin sponsor?')) {
      try {
        setUpdating(true);
        
        if (sponsor._id) {
          // Delete using backend API
          const response = await deleteAdminSponsor(id!, sponsor._id);
          if (response.success) {
            setSuccess('Admin sponsor deleted successfully');
            // Reload sponsors to reflect deletion
            await loadAdminSponsors();
            setTimeout(() => setSuccess(null), 3000);
          } else {
            setError(response.message || 'Failed to delete admin sponsor');
            setTimeout(() => setError(null), 5000);
          }
        } else {
          // Fallback for sponsors without _id (should not happen with backend)
          console.warn('Sponsor missing _id, cannot delete from backend');
          setError('Cannot delete sponsor: Invalid sponsor data');
          setTimeout(() => setError(null), 5000);
        }
      } catch (error: any) {
        console.error('Error deleting admin sponsor:', error);
        setError(error.response?.data?.message || 'Failed to delete admin sponsor');
        setTimeout(() => setError(null), 5000);
      } finally {
        setUpdating(false);
      }
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset to original values
      setEditData({
        status: hackathon.status || 'pending',
        featured: hackathon.featured || false,
        takeRegistrations: hackathon.takeRegistrations !== false
      });
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    try {
      setUpdating(true);
      const response = await updateHackathon(id!, editData);
      if (response.success) {
        setHackathon(response.data);
        setSuccess('Hackathon updated successfully');
        setIsEditing(false);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to update hackathon');
      }
    } catch (err) {
      setError('Error updating hackathon');
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this hackathon? This action cannot be undone.')) {
      try {
        setUpdating(true);
        const response = await deleteHackathon(id!);
        if (response.success) {
          navigate('/hackathon-analytics');
        } else {
          setError('Failed to delete hackathon');
        }
      } catch (err) {
        setError('Error deleting hackathon');
        console.error(err);
      } finally {
        setUpdating(false);
      }
    }
  };

  const getStatusColor = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option?.color || 'default';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted': return <CheckCircle />;
      case 'pending': return <Warning />;
      case 'rejected': return <Error />;
      default: return <CheckCircle />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!hackathon) {
    return (
      <Box p={3}>
        <Alert severity="error">Hackathon not found</Alert>
      </Box>
    );
  }

  return (
    <Box maxWidth={1200} mx="auto" p={2}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/hackathon-analytics')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight={700} color="primary">
          Hackathon Details
        </Typography>
        <Box ml="auto" display="flex" gap={1}>
          <Button
            variant={isEditing ? "outlined" : "contained"}
            startIcon={isEditing ? <Cancel /> : <Edit />}
            onClick={handleEditToggle}
            disabled={updating}
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
          {isEditing && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={updating}
            >
              Save
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleDelete}
            disabled={updating}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
        {/* Main Content */}
        <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 8' } }}>
          {/* Hackathon Card */}
          <Card sx={{ mb: 3 }}>
            <Box sx={{ position: 'relative' }}>
              {hackathon.images?.banner || hackathon.banner ? (
                <CardMedia
                  component="img"
                  height="200"
                  image={hackathon.images?.banner || hackathon.banner}
                  alt={hackathon.title}
                />
              ) : (
                <Box
                  sx={{
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #5b7fff 0%, #6ec6ff 100%)',
                  }}
                >
                  <Typography variant="h5" color="white" fontWeight={700}>
                    {hackathon.title}
                  </Typography>
                </Box>
              )}
              
              {/* Status and Featured Chips */}
              <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1 }}>
                {hackathon.featured && (
                  <Chip
                    icon={<Star />}
                    label="Featured"
                    color="warning"
                    size="small"
                  />
                )}
                <Chip
                  icon={getStatusIcon(hackathon.status)}
                  label={hackathon.status}
                  color={getStatusColor(hackathon.status) as any}
                  size="small"
                />
              </Box>
            </Box>

            <CardContent>
              <Typography variant="h5" fontWeight={600} mb={2}>
                {hackathon.title}
              </Typography>
              
              {/* Admin Settings Section */}
              {isEditing ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    Admin Settings
                  </Typography>
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={editData.status}
                          onChange={(e) => setEditData(prev => ({ ...prev, status: e.target.value }))}
                          label="Status"
                        >
                          {statusOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editData.featured}
                            onChange={(e) => setEditData(prev => ({ ...prev, featured: e.target.checked }))}
                          />
                        }
                        label="Featured"
                      />
                    </Box>
                    
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editData.takeRegistrations}
                            onChange={(e) => setEditData(prev => ({ ...prev, takeRegistrations: e.target.checked }))}
                          />
                        }
                        label="Take Registrations"
                      />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    Admin Settings
                  </Typography>
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                      <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                      <Chip
                        icon={getStatusIcon(hackathon.status)}
                        label={hackathon.status}
                        color={getStatusColor(hackathon.status) as any}
                        size="small"
                      />
                    </Box>
                    
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                      <Typography variant="subtitle2" color="text.secondary">Featured</Typography>
                      <Chip
                        icon={hackathon.featured ? <Star /> : undefined}
                        label={hackathon.featured ? 'Yes' : 'No'}
                        color={hackathon.featured ? 'warning' : 'default'}
                        size="small"
                      />
                    </Box>
                    
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                      <Typography variant="subtitle2" color="text.secondary">Take Registrations</Typography>
                      <Chip
                        icon={hackathon.takeRegistrations !== false ? <CheckCircle /> : <Error />}
                        label={hackathon.takeRegistrations !== false ? 'Yes' : 'No'}
                        color={hackathon.takeRegistrations !== false ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Description with Rich Text Formatting */}
              <Typography variant="h6" fontWeight={600} mb={2}>
                Description
              </Typography>
              <Box 
                sx={{ 
                  mb: 3,
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    fontWeight: 600,
                    mb: 1
                  },
                  '& p': {
                    mb: 1
                  },
                  '& ul, & ol': {
                    pl: 2,
                    mb: 1
                  },
                  '& li': {
                    mb: 0.5
                  },
                  '& strong, & b': {
                    fontWeight: 600
                  },
                  '& em, & i': {
                    fontStyle: 'italic'
                  },
                  '& a': {
                    color: 'primary.main',
                    textDecoration: 'underline'
                  },
                  '& blockquote': {
                    borderLeft: '4px solid',
                    borderColor: 'grey.300',
                    pl: 2,
                    ml: 0,
                    fontStyle: 'italic'
                  }
                }}
                dangerouslySetInnerHTML={{ __html: hackathon.description }}
              />

              {/* Hackathon Details */}
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">Start Date</Typography>
                  </Box>
                  <Typography variant="body2">
                    {new Date(hackathon.dates?.startDate).toLocaleDateString()}
                  </Typography>
                </Box>
                
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">End Date</Typography>
                  </Box>
                  <Typography variant="body2">
                    {new Date(hackathon.dates?.endDate).toLocaleDateString()}
                  </Typography>
                </Box>
                
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">Registration Deadline</Typography>
                  </Box>
                  <Typography variant="body2">
                    {new Date(hackathon.dates?.registrationDeadline).toLocaleDateString()}
                  </Typography>
                </Box>
                
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">Created Date</Typography>
                  </Box>
                  <Typography variant="body2">
                    {new Date(hackathon.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                  </Box>
                  <Typography variant="body2">
                    {hackathon.location ? 
                      `${hackathon.location.type}${hackathon.location.address ? ` - ${hackathon.location.address}` : ''}${hackathon.location.state ? `, ${hackathon.location.state}` : ''}${hackathon.location.country ? `, ${hackathon.location.country}` : ''}` 
                      : 'Online'
                    }
                  </Typography>
                </Box>
                
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Business sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">Organizer</Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'primary.main', 
                      cursor: 'pointer', 
                      textDecoration: 'underline',
                      '&:hover': { textDecoration: 'none' }
                    }}
                    onClick={() => window.open(`http://localhost:3000/in/${hackathon.createdBy?.username}`, '_blank')}
                  >
                    {hackathon.createdBy?.firstName} {hackathon.createdBy?.lastName}
                  </Typography>
                </Box>
                
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <AttachMoney sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">Registration Fee</Typography>
                  </Box>
                  <Typography variant="body2">
                    {hackathon.pricing?.type === 'free' ? 'Free' : `$${hackathon.pricing?.amount || 0}`}
                  </Typography>
                </Box>

                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <People sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">Participants</Typography>
                  </Box>
                  <Typography variant="body2">
                    {hackathon.currentParticipants || 0} / {hackathon.maxParticipants || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Admin Sponsor Management */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                  Admin Sponsors Management
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddSponsor}
                  disabled={updating || sponsorsLoading}
                  size="small"
                >
                  Add Admin Sponsor
                </Button>
              </Box>

              {/* Loading State for Sponsors */}
              {sponsorsLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 2 }} color="text.secondary">
                    Loading admin sponsors...
                  </Typography>
                </Box>
              ) : adminSponsors.length > 0 ? (
                <Box 
                  sx={{ 
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(3, 1fr)'
                    },
                    gap: 2
                  }}
                >
                  {adminSponsors.map((sponsor, index) => (
                    <Card 
                      key={sponsor._id || index}
                      variant="outlined" 
                      sx={{ 
                        height: '100%',
                        border: 2,
                        borderColor: 'primary.200',
                        bgcolor: 'primary.50',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                          borderColor: 'primary.400'
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        {/* Logo */}
                        {sponsor.logo ? (
                          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            <img 
                              src={sponsor.logo}
                              alt={sponsor.name}
                              style={{ 
                                width: '60px', 
                                height: '60px', 
                                objectFit: 'contain',
                                borderRadius: '8px'
                              }}
                            />
                          </Box>
                        ) : (
                          <Avatar 
                            sx={{ 
                              width: 60, 
                              height: 60, 
                              mx: 'auto', 
                              mb: 2, 
                              bgcolor: 'primary.200'
                            }}
                          >
                            <Business />
                          </Avatar>
                        )}

                        {/* Name */}
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                          {sponsor.name}
                        </Typography>

                        {/* Website */}
                        {sponsor.website && (
                          <Typography 
                            variant="body2" 
                            color="primary.main" 
                            sx={{ 
                              mb: 2,
                              textDecoration: 'underline', 
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                              '&:hover': { color: 'primary.dark' }
                            }}
                            onClick={() => {
                              const website = sponsor.website!;
                              window.open(
                                website.startsWith('http') 
                                  ? website 
                                  : `https://${website}`, 
                                '_blank'
                              );
                            }}
                          >
                            <Language fontSize="small" />
                            Website
                          </Typography>
                        )}

                        {/* Admin Badge */}
                        <Chip
                          label="Admin Sponsor"
                          size="small"
                          color="primary"
                          variant="filled"
                          sx={{ 
                            mb: 2,
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}
                        />

                        {/* Action Buttons */}
                        <Box display="flex" justifyContent="center" gap={1}>
                          <Tooltip title="Edit Sponsor">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditSponsor(sponsor, index)}
                              disabled={updating}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Sponsor">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteSponsor(sponsor, index)}
                              disabled={updating}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Box 
                  sx={{ 
                    textAlign: 'center', 
                    py: 4,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: '2px dashed',
                    borderColor: 'grey.300'
                  }}
                >
                  <Business sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" mb={1}>
                    No Admin Sponsors Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Add admin sponsors to showcase official partners
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddSponsor}
                    disabled={updating}
                  >
                    Add First Admin Sponsor
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Categories and Tags */}
          {hackathon.categories && hackathon.categories.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  Categories
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {hackathon.categories.map((category: string, index: number) => (
                    <Chip
                      key={index}
                      label={category.replace('_', ' ')}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Sidebar - Analytics and Participants */}
        <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
          {/* Analytics Card */}
          {analytics && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  Analytics
                </Typography>
                
                <Box display="flex" alignItems="center" mb={2}>
                  <People sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {analytics.totalParticipants} participants
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" mb={2}>
                  <TrendingUp sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {analytics.daysUntilStart > 0 ? `${analytics.daysUntilStart} days until start` : 'Started'}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" mb={2}>
                  <CalendarToday sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {analytics.daysUntilEnd > 0 ? `${analytics.daysUntilEnd} days until end` : 'Ended'}
                  </Typography>
                </Box>
                
                <Chip
                  label={
                    analytics.isActive ? 'Active' :
                    analytics.isUpcoming ? 'Upcoming' :
                    analytics.isCompleted ? 'Completed' :
                    analytics.isExpired ? 'Expired' : 'Unknown'
                  }
                  color={
                    analytics.isActive ? 'success' :
                    analytics.isUpcoming ? 'info' :
                    analytics.isCompleted ? 'default' :
                    analytics.isExpired ? 'error' : 'default'
                  }
                  size="small"
                />
              </CardContent>
            </Card>
          )}

          {/* Participants Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Participants ({hackathon.participants?.length || 0})
              </Typography>
              
              {hackathon.participants && hackathon.participants.length > 0 ? (
                <List>
                  {hackathon.participants.slice(0, 5).map((participant: any) => (
                    <ListItem key={participant._id || participant.user?._id} dense>
                      <ListItemAvatar>
                        <Avatar src={participant.user?.profilePicture}>
                          {participant.user?.firstName?.[0] || '?'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${participant.user?.firstName || 'Unknown'} ${participant.user?.lastName || ''}`}
                        secondary={`@${participant.user?.username || 'unknown'}`}
                      />
                    </ListItem>
                  ))}
                  {hackathon.participants.length > 5 && (
                    <ListItem>
                      <ListItemText
                        primary={`+${hackathon.participants.length - 5} more participants`}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No participants yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Admin Sponsor Dialog */}
      <Dialog 
        open={sponsorDialog} 
        onClose={() => setSponsorDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Business color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {editingSponsor ? 'Edit Admin Sponsor' : 'Add Admin Sponsor'}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              label="Sponsor Name"
              value={sponsorForm.name}
              onChange={(e) => setSponsorForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
              variant="outlined"
              placeholder="Enter sponsor company name"
            />
            
            <TextField
              label="Website URL"
              value={sponsorForm.website}
              onChange={(e) => setSponsorForm(prev => ({ ...prev, website: e.target.value }))}
              fullWidth
              variant="outlined"
              placeholder="https://example.com"
              InputProps={{
                startAdornment: <Language sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              helperText="Optional: Company website URL"
            />
            
            <TextField
              label="Logo Image URL"
              value={sponsorForm.logo}
              onChange={(e) => setSponsorForm(prev => ({ ...prev, logo: e.target.value }))}
              fullWidth
              variant="outlined"
              placeholder="https://example.com/logo.png"
              InputProps={{
                startAdornment: <PhotoLibrary sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              helperText="Optional: Direct URL to sponsor logo image"
            />
            
            {/* Preview */}
            {(sponsorForm.name || sponsorForm.logo) && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  Preview:
                </Typography>
                <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'grey.200', borderRadius: 1, bgcolor: 'white' }}>
                  {sponsorForm.logo ? (
                    <img 
                      src={sponsorForm.logo}
                      alt={sponsorForm.name}
                      style={{ 
                        width: '60px', 
                        height: '60px', 
                        objectFit: 'contain',
                        borderRadius: '8px',
                        marginBottom: '8px'
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 1, bgcolor: 'primary.200' }}>
                      <Business />
                    </Avatar>
                  )}
                  {sponsorForm.name && (
                    <Typography variant="subtitle1" fontWeight={600}>
                      {sponsorForm.name}
                    </Typography>
                  )}
                  <Chip
                    label="Admin Sponsor"
                    size="small"
                    color="primary"
                    variant="filled"
                    sx={{ mt: 1, fontSize: '0.7rem', fontWeight: 600 }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setSponsorDialog(false)}
            disabled={updating}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveSponsor}
            variant="contained"
            disabled={updating || !sponsorForm.name.trim()}
            startIcon={updating ? <CircularProgress size={16} /> : <Save />}
          >
            {updating ? 'Saving...' : (editingSponsor ? 'Update' : 'Add')} Sponsor
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HackathonDetailPage;